import { useEffect, useMemo, useState } from "react";

import { isProbablyText } from "./is-probably-text";

const SNIFF_TIMEOUT_MS = 10_000;

/**
 * Sniffs whether viewer content is text-like. Byte content is sampled
 * synchronously; string content is a fetchable URL (see useFileContent), so its
 * first chunk is fetched and sampled. Returns undefined while that is pending.
 */
export function useIsProbablyText(content: string | Uint8Array | null, enabled: boolean): boolean | undefined {
  const syncResult = useMemo(() => {
    if (!enabled || content == null || typeof content === "string") return undefined;
    return isProbablyText(content);
  }, [content, enabled]);

  // Keyed by URL so a result for a previous file is never returned for the
  // current one, even for the render before the effect fires.
  const [asyncState, setAsyncState] = useState<{ url: string; result: boolean } | null>(null);

  useEffect(() => {
    if (!enabled || typeof content !== "string" || content.length === 0) return;

    const abortController = new AbortController();
    const signal = AbortSignal.any([abortController.signal, AbortSignal.timeout(SNIFF_TIMEOUT_MS)]);
    fetch(content, { signal })
      .then(async (response) => {
        if (!response.ok) return false;
        const reader = response.body?.getReader();
        if (!reader) return false;
        const { value } = await reader.read();
        await reader.cancel();
        return isProbablyText(value ?? null);
      })
      .then(
        (result) => {
          if (!abortController.signal.aborted) setAsyncState({ url: content, result });
        },
        () => {
          // Timeouts land here with the component still mounted; only skip the
          // update when the effect itself was cleaned up.
          if (!abortController.signal.aborted) setAsyncState({ url: content, result: false });
        },
      );

    return () => abortController.abort();
  }, [content, enabled]);

  if (!enabled || content == null || (typeof content === "string" && content.length === 0)) return false;
  if (typeof content !== "string") return syncResult;
  return asyncState?.url === content ? asyncState.result : undefined;
}
